import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Toolbar,
  useTheme,
  useMediaQuery,
  Badge,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Dashboard,
  People,
  CalendarMonth,
  Receipt,
  Folder,
  Timer,
  Description,
  Mail,
  BarChart,
  Settings,
  Logout,
  Close,
  EventNote,
  Web,
  Category,
  ContactPage,
  Checklist,
  StickyNote2 as NotesIcon,
  Alarm as RemindersIcon,
  Forum as CommunityIcon,
  AdminPanelSettings,
  Lock,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS } from '@elevair/shared';
import AvatarWithBadge from '../common/AvatarWithBadge';
import { SubscriptionTier, UserRole, tierDisplayNames } from '../common/TierBadge';
import { contractsApi } from '../../services/contractsApi';
import { getTierIcon, getTierColor, mapLegacyTier } from '../icons/TierIcons';
import { useSubscription } from '../../hooks/useSubscription';

const DRAWER_WIDTH = 280;

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

// Tier hierarchy for feature gating
const TIER_HIERARCHY: SubscriptionTier[] = ['expired', 'free', 'trial', 'launch', 'rise', 'summit'];

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  // Minimum tier required to access this feature
  requiredTier?: SubscriptionTier;
  // Key to look up dynamic badge count
  badgeKey?: 'contractsPending';
}

const mainNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
  { label: 'Clients', path: '/clients', icon: <People /> },
  { label: 'Services', path: '/services', icon: <Category /> },
  { label: 'Calendar', path: '/calendar', icon: <CalendarMonth /> },
  { label: 'Bookings', path: '/bookings', icon: <EventNote /> },
  { label: 'Tasks', path: '/tasks', icon: <Checklist /> },
  { label: 'Notes', path: '/notes', icon: <NotesIcon /> },
  { label: 'Reminders', path: '/reminders', icon: <RemindersIcon /> },
  { label: 'Invoices', path: '/invoices', icon: <Receipt /> },
  { label: 'Projects', path: '/projects', icon: <Folder /> },
  { label: 'Time Tracking', path: '/time-tracking', icon: <Timer /> },
  { label: 'Contracts', path: '/contracts', icon: <Description />, requiredTier: 'rise', badgeKey: 'contractsPending' },
  { label: 'Messages', path: '/messages', icon: <Mail /> },
];

const secondaryNavItems: NavItem[] = [
  { label: 'Landing Page', path: '/landing-page', icon: <Web /> },
  { label: 'Leads', path: '/leads', icon: <ContactPage /> },
  { label: 'Community Hub', path: '/community', icon: <CommunityIcon /> },
  { label: 'Analytics', path: '/analytics', icon: <BarChart /> },
  { label: 'Payment Settings', path: '/settings/payments', icon: <CreditCardIcon /> },
  { label: 'Settings', path: '/settings', icon: <Settings /> },
];

export default function Sidebar({ open, onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, organization, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch fresh subscription data to ensure we always display the correct tier
  const { data: subscriptionData } = useSubscription();

  // Get current tier - prefer fresh API data over potentially stale AuthContext data
  const currentTier = (subscriptionData?.subscription?.tier || organization?.subscription?.tier || 'free') as SubscriptionTier;
  const currentTierIndex = TIER_HIERARCHY.indexOf(currentTier);

  // Check if user has contracts feature enabled
  const hasContractsEnabled = organization?.boldsign?.contractsEnabled === true;

  // Fetch contract stats for badge (only if contracts are enabled)
  const { data: contractStats } = useQuery({
    queryKey: ['contractStats'],
    queryFn: contractsApi.getStats,
    enabled: hasContractsEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });

  // Helper to check if user meets tier requirement
  const meetsTierRequirement = (requiredTier?: SubscriptionTier): boolean => {
    if (!requiredTier) return true;
    const requiredIndex = TIER_HIERARCHY.indexOf(requiredTier);
    return currentTierIndex >= requiredIndex;
  };

  // Get badge count for a nav item
  const getBadgeCount = (badgeKey?: NavItem['badgeKey']): number | undefined => {
    if (!badgeKey) return undefined;
    if (badgeKey === 'contractsPending' && contractStats) {
      return contractStats.pendingCount > 0 ? contractStats.pendingCount : undefined;
    }
    return undefined;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#FFFFFF',
      }}
    >
      {/* Spacer for fixed AppBar */}
      <Toolbar />

      {/* User Info */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
          backgroundColor: COLORS.background,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'hidden' }}>
          <AvatarWithBadge
            name={`${user?.profile.firstName} ${user?.profile.lastName}`}
            avatarUrl={user?.profile.avatar}
            avatarColor={user?.profile.avatarColor || COLORS.primary}
            tier={currentTier}
            userRole={user?.role as UserRole}
            size={44}
            onAvatarClick={() => handleNavigation('/settings/profile')}
            onBadgeClick={() => handleNavigation('/settings/billing')}
          />
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {user?.profile.firstName} {user?.profile.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {organization?.name}
            </Typography>
          </Box>
        </Box>
        {isMobile && (
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        )}
      </Box>

      {/* Main Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        <List disablePadding>
          {mainNavItems.map((item) => {
            const hasTierAccess = meetsTierRequirement(item.requiredTier);
            const isLocked = !hasTierAccess;
            const badgeCount = getBadgeCount(item.badgeKey);

            // For contracts, also check if feature is enabled
            const isContractsItem = item.path === '/contracts';
            const showContractsBadge = isContractsItem && hasContractsEnabled && badgeCount;

            return (
              <ListItem key={item.path} disablePadding>
                <Tooltip
                  title={isLocked ? `Requires ${item.requiredTier} plan or higher` : ''}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    onClick={() => !isLocked && handleNavigation(item.path)}
                    selected={location.pathname === item.path}
                    disabled={isLocked}
                    sx={{
                      mx: 1,
                      borderRadius: 2,
                      mb: 0.5,
                      opacity: isLocked ? 0.6 : 1,
                      '&.Mui-selected': {
                        backgroundColor: `${COLORS.primary}15`,
                        '&:hover': {
                          backgroundColor: `${COLORS.primary}25`,
                        },
                        '& .MuiListItemIcon-root': {
                          color: COLORS.primary,
                        },
                        '& .MuiListItemText-primary': {
                          color: COLORS.primary,
                          fontWeight: 600,
                        },
                      },
                      '&.Mui-disabled': {
                        opacity: 0.6,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: location.pathname === item.path
                          ? COLORS.primary
                          : 'text.secondary',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                      }}
                    />
                    {/* Show lock icon for tier-gated features */}
                    {isLocked && (
                      <Lock sx={{ fontSize: 16, color: 'text.disabled', ml: 1 }} />
                    )}
                    {/* Show tier chip for locked features */}
                    {isLocked && item.requiredTier && (
                      <Chip
                        label={item.requiredTier}
                        size="small"
                        sx={{
                          ml: 1,
                          height: 20,
                          fontSize: '0.65rem',
                          textTransform: 'capitalize',
                          backgroundColor: 'grey.200',
                          color: 'text.secondary',
                        }}
                      />
                    )}
                    {/* Show badge for pending contracts */}
                    {showContractsBadge && (
                      <Badge
                        badgeContent={badgeCount}
                        color="warning"
                        sx={{
                          ml: 1,
                          '& .MuiBadge-badge': {
                            fontSize: '0.7rem',
                            height: 18,
                            minWidth: 18,
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Secondary Navigation */}
        <List disablePadding>
          {secondaryNavItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: `${COLORS.primary}15`,
                    '&:hover': {
                      backgroundColor: `${COLORS.primary}25`,
                    },
                    '& .MuiListItemIcon-root': {
                      color: COLORS.primary,
                    },
                    '& .MuiListItemText-primary': {
                      color: COLORS.primary,
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: location.pathname === item.path
                      ? COLORS.primary
                      : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Platform Admin - Super Admin Only */}
        {user?.role === 'super_admin' && (
          <>
            <Divider sx={{ my: 2 }} />
            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation('/platform-admin')}
                  selected={location.pathname === '/platform-admin'}
                  sx={{
                    mx: 1,
                    borderRadius: 2,
                    mb: 0.5,
                    backgroundColor: location.pathname === '/platform-admin'
                      ? 'warning.light'
                      : 'transparent',
                    '&.Mui-selected': {
                      backgroundColor: 'warning.light',
                      '&:hover': {
                        backgroundColor: 'warning.main',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'warning.dark',
                      },
                      '& .MuiListItemText-primary': {
                        color: 'warning.dark',
                        fontWeight: 600,
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'warning.light',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: location.pathname === '/platform-admin'
                        ? 'warning.dark'
                        : 'warning.main',
                    }}
                  >
                    <AdminPanelSettings />
                  </ListItemIcon>
                  <ListItemText
                    primary="Platform Admin"
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: location.pathname === '/platform-admin' ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        )}
      </Box>

      {/* Logout Button */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'error.light',
              color: 'error.main',
              '& .MuiListItemIcon-root': {
                color: 'error.main',
              },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </Box>

      {/* Plan Badge */}
      <Box
        onClick={() => handleNavigation('/settings/billing')}
        sx={{
          p: 2,
          backgroundColor: COLORS.background,
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: `${COLORS.primary}08`,
          },
          transition: 'background-color 0.2s',
        }}
      >
        {(() => {
          // Use fresh subscription data, falling back to organization data
          const rawTier = subscriptionData?.subscription?.tier || organization?.subscription?.tier || 'free';
          const tier = mapLegacyTier(rawTier);
          const TierIcon = getTierIcon(tier);
          const tierColor = getTierColor(tier);
          const displayName = user?.role === 'super_admin'
            ? 'Platform Master'
            : tierDisplayNames[tier] || 'Free Plan';
          const showUpgrade = tier !== 'summit' && tier !== 'platinum' && user?.role !== 'super_admin';

          return (
            <>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                <TierIcon sx={{ fontSize: 18, color: tierColor }} />
                <Typography variant="caption" color="text.secondary">
                  {displayName}
                </Typography>
              </Box>
              {showUpgrade && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{
                    color: tierColor,
                    mt: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Upgrade
                </Typography>
              )}
            </>
          );
        })()}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={open}
      onClose={onClose}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
